import { sanitizeLoginNextPath } from '@/lib/loginNavigation';
import {
  parseSignupServiceIntent,
  SIGNUP_INTENT_VENDOR_ONBOARD,
  signupUsesVendorOnboarding,
  type SignupServiceIntent,
} from '@shopgnd/shared';
import { buildJoinPath, joinPathFromGoals } from '@/lib/joinPaths';

/** Post-sign-up paths from registration goal selection (buyer is always implied). */

export type SignupGoals = {
  sell: boolean;
  /** Trades / Find a Pro — completes pro onboarding (vendor shell auto-created). */
  pro: boolean;
  /** Non-trade service businesses route to vendor onboarding with preset category. */
  serviceIntent: SignupServiceIntent | null;
};

export const SHOP_ONLY_GOALS: SignupGoals = { sell: false, pro: false, serviceIntent: null };

export function isShopOnly(goals: SignupGoals): boolean {
  return !goals.sell && !goals.pro && !goals.serviceIntent;
}

export function parseSignupGoalsFromIntent(intent: string | null): SignupGoals {
  const serviceIntent = parseSignupServiceIntent(intent);
  if (serviceIntent) {
    if (serviceIntent === 'trades') {
      return { sell: false, pro: true, serviceIntent: 'trades' };
    }
    return { sell: true, pro: false, serviceIntent };
  }
  if (!intent) return { ...SHOP_ONLY_GOALS };
  const normalized = intent.toLowerCase().replace(/\s+/g, '');
  if (normalized === 'business' || normalized === 'sell+pro' || normalized === 'sell,pro') {
    return { sell: true, pro: true, serviceIntent: null };
  }
  if (normalized === 'sell') return { sell: true, pro: false, serviceIntent: null };
  if (normalized === 'pro' || normalized === 'services') {
    return { sell: false, pro: true, serviceIntent: 'trades' };
  }
  return { ...SHOP_ONLY_GOALS };
}

/** @deprecated Prefer buildJoinPath / joinPathFromGoals — kept for callers expecting vendor URL shape. */
export function vendorOnboardPathForServiceIntent(
  intent: SignupServiceIntent,
  planId?: string | null,
  next?: string | null,
): string {
  if (intent === 'trades') return buildJoinPath({ intent: 'pro', plan: planId, next });
  const preset = SIGNUP_INTENT_VENDOR_ONBOARD[intent];
  return buildJoinPath({
    intent,
    plan: planId,
    next,
    extra: {
      sector: preset.sector,
      category: preset.directoryCategoryId,
      modes: preset.modes.join(','),
    },
  });
}

export function resolvePostSignupPath(
  goals: SignupGoals,
  nextParam: string | null,
  planId?: string | null,
): string {
  const safeNext = sanitizeLoginNextPath(nextParam);
  if (safeNext) return safeNext;
  if (isShopOnly(goals)) return '/';
  if (goals.serviceIntent && signupUsesVendorOnboarding(goals.serviceIntent)) {
    return vendorOnboardPathForServiceIntent(goals.serviceIntent, planId);
  }
  return joinPathFromGoals(goals, planId);
}

export function needsBusinessNameAtSignup(goals: SignupGoals): boolean {
  return goals.sell || signupUsesVendorOnboarding(goals.serviceIntent);
}

export function signupCtaLabel(goals: SignupGoals): string {
  if (isShopOnly(goals)) return 'Create account & start shopping';
  if (goals.sell && goals.pro) return 'Create account & set up store and services';
  if (goals.serviceIntent === 'rentals') return 'Create account & set up rentals';
  if (goals.serviceIntent === 'transport') return 'Create account & set up transport';
  if (goals.serviceIntent === 'appointments') return 'Create account & set up bookings';
  if (goals.serviceIntent === 'creative') return 'Create account & set up your services';
  if (goals.sell) return 'Create account & open my store';
  if (goals.pro) return 'Create account & list trades & repairs';
  return 'Create account & list my services';
}

export function signupGoalsSummary(goals: SignupGoals): string {
  if (isShopOnly(goals)) return 'Shop and buy from local vendors across Grenada.';
  if (goals.sell && goals.pro) {
    return 'Set up your online store and your professional services profile — you can use both on one account.';
  }
  if (goals.serviceIntent === 'rentals') {
    return 'After verification, set up your rental business with a public profile and booking-ready storefront.';
  }
  if (goals.serviceIntent === 'transport') {
    return 'After verification, set up transport or shuttle services with appointments and quotes.';
  }
  if (goals.serviceIntent === 'appointments') {
    return 'After verification, set up your salon, spa, or appointment-based business.';
  }
  if (goals.serviceIntent === 'creative') {
    return 'After verification, set up your events, photo, or creative services profile.';
  }
  if (goals.serviceIntent === 'trades' || goals.pro) {
    return 'After verification, create your trades profile on Find a Pro with a shareable business page.';
  }
  if (goals.sell) return 'After email verification, you will set up your store and listing details.';
  return 'After email verification, you will create your services profile.';
}
